import api from './apiConfig';

const BASE_URL = '/domain-registries';

const registryService = {
  async getAll(page = 0, size = 10, search = '') {
    try {
      const term = (search || '').trim();
      const params = term ? { page, size, anyMatch: term } : { page, size };
      const response = await api.get(BASE_URL, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching registries:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const response = await api.get(`${BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching registry ${id}:`, error);
      throw error;
    }
  },

  async getByCode(code) {
    try {
      const response = await api.get(`${BASE_URL}/code/${code}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching registry by code ${code}:`, error);
      throw error;
    }
  },

  async lookup(registryId, field, q, limit = 10) {
    try {
      const response = await api.get(`${BASE_URL}/${registryId}/lookup`, {
        params: { field, q, limit },
      });
      return response.data;
    } catch (error) {
      console.error(`Error looking up ${field} in registry ${registryId}:`, error);
      throw error;
    }
  },

  async create(data) {
    try {
      const response = await api.post(BASE_URL, data);
      return response.data;
    } catch (error) {
      console.error('Error creating registry:', error);
      throw error;
    }
  },

  async update(id, data) {
    try {
      const response = await api.put(`${BASE_URL}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating registry ${id}:`, error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const response = await api.delete(`${BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting registry ${id}:`, error);
      throw error;
    }
  },

  // ----- Entry operations (keyed by entryId) -----
  async getEntries(registryId, params = {}) {
    try {
      const response = await api.get(`${BASE_URL}/${registryId}/entries`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching entries for registry ${registryId}:`, error);
      throw error;
    }
  },

  async getEntryById(registryId, entryId) {
    try {
      const response = await api.get(`${BASE_URL}/${registryId}/entries/${entryId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching entry ${entryId}:`, error);
      throw error;
    }
  },

  async createEntry(registryId, attributes) {
    try {
      const response = await api.post(`${BASE_URL}/${registryId}/entries`, { attributes });
      return response.data;
    } catch (error) {
      console.error(`Error creating entry for registry ${registryId}:`, error);
      throw error;
    }
  },

  async updateEntry(registryId, entryId, attributes) {
    try {
      const response = await api.put(`${BASE_URL}/${registryId}/entries/${entryId}`, { attributes });
      return response.data;
    } catch (error) {
      console.error(`Error updating entry ${entryId}:`, error);
      throw error;
    }
  },

  async deleteEntry(registryId, entryId) {
    try {
      const response = await api.delete(`${BASE_URL}/${registryId}/entries/${entryId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting entry ${entryId}:`, error);
      throw error;
    }
  },

  // ----- Import / export / storico delle voci -----
  async previewImport(id, file) {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post(`${BASE_URL}/${id}/entries/import/preview`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error(`Error previewing import for registry ${id}:`, error);
      throw error;
    }
  },

  async importEntries(id, file, { fingerprint, skipInvalid = false } = {}) {
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('fingerprint', fingerprint);
      form.append('skipInvalid', skipInvalid ? 'true' : 'false');
      const response = await api.post(`${BASE_URL}/${id}/entries/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error(`Error importing entries for registry ${id}:`, error);
      throw error;
    }
  },

  /** Restituisce la risposta intera (non solo `data`): il nome del file sta negli header. */
  async exportEntries(id, { format = 'csv', params = {} } = {}) {
    try {
      return await api.get(`${BASE_URL}/${id}/entries/export`, {
        params: { ...params, format },
        responseType: 'blob',
      });
    } catch (error) {
      console.error(`Error exporting entries for registry ${id}:`, error);
      throw error;
    }
  },

  async getEntryHistory(id, { page = 0, size = 20, entryId } = {}) {
    try {
      const params = { page, size };
      if (entryId) params.entryId = entryId;
      const response = await api.get(`${BASE_URL}/${id}/entries/history`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching entry history for registry ${id}:`, error);
      throw error;
    }
  },

  // ----- Sync operations -----
  async startSync(id) {
    try {
      const response = await api.post(`${BASE_URL}/${id}/sync`);
      return response.data;
    } catch (error) {
      console.error(`Error starting sync for registry ${id}:`, error);
      throw error;
    }
  },

  async uploadCsv(id, file) {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post(`${BASE_URL}/${id}/sync/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading CSV for registry ${id}:`, error);
      throw error;
    }
  },

  async getSyncRuns(id, { page = 0, size = 20 } = {}) {
    try {
      const response = await api.get(`${BASE_URL}/${id}/sync/runs`, { params: { page, size } });
      return response.data;
    } catch (error) {
      console.error(`Error fetching sync runs for registry ${id}:`, error);
      throw error;
    }
  },

  async getSyncRun(id, runId) {
    try {
      const response = await api.get(`${BASE_URL}/${id}/sync/runs/${runId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching sync run ${runId}:`, error);
      throw error;
    }
  },
};

export default registryService;
